import Bugsnag from '@bugsnag/js';

type Metadata = Record<string, unknown>;

const DEFAULT_ERROR_NAME = 'NonErrorRejection';

const AUTH_CONTEXT = 'auth-flow';
const SENSITIVE_KEYS = ['token', 'code', 'password', 'credential', 'secret'];

export function leaveAuthBreadcrumb(message: string, metadata?: Metadata): void {
  if (!Bugsnag.isStarted()) return;
  Bugsnag.leaveBreadcrumb(message, normalizeMetadata(metadata));
}

export function notifyAuthError(error: unknown, metadata?: Metadata): void {
  if (!Bugsnag.isStarted()) return;
  const normalizedMetadata = normalizeMetadata(metadata);
  const errorToNotify = normalizeError(error, 'Auth flow error', 'AuthFlowError');

  Bugsnag.notify(errorToNotify, (event) => {
    event.context = AUTH_CONTEXT;

    if (normalizedMetadata) {
      event.addMetadata('auth', normalizedMetadata);
    }
  });
}

export function sanitizeEmail(email?: string | null): string | null {
  if (!email) {
    return null;
  }

  const [localPart, domain = ''] = email.split('@');

  if (!localPart) {
    return null;
  }

  const prefix = localPart.slice(0, 2);
  const mask = localPart.length > 2 ? `${prefix}***` : `${localPart[0] ?? ''}***`;

  return domain ? `${mask}@${domain}` : `${mask}`;
}

export function summarizePayload(payload: unknown): Record<string, unknown> | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  if (payload instanceof FormData) {
    const summary: Record<string, unknown> = {};

    payload.forEach((_value, key) => {
      summary[key] = formValueSummary(key);
    });

    return summary;
  }

  return Object.entries(payload as Record<string, unknown>).reduce<Record<string, unknown>>(
    (acc, [key, value]) => reducePayloadEntry(acc, key, value),
    {},
  );
}

export function normalizeError(
  reason: unknown,
  fallbackMessage = 'Unexpected error',
  fallbackName = DEFAULT_ERROR_NAME,
): Error {
  if (reason instanceof Error) {
    return reason;
  }

  let message = fallbackMessage;
  let name = fallbackName;

  if (typeof reason === 'string') {
    message = reason;
  } else if (reason && typeof reason === 'object') {
    const candidate = reason as { message?: unknown; name?: unknown };

    if (typeof candidate.message === 'string') {
      message = candidate.message;
    }

    if (typeof candidate.name === 'string') {
      name = candidate.name;
    }
  }

  const error = new Error(message);

  error.name = name;

  return error;
}

function normalizeMetadata(metadata?: Metadata): Metadata | undefined {
  if (!metadata) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(metadata).filter(([, value]) => value !== undefined),
  );
}

function reducePayloadEntry(
  accumulator: Record<string, unknown>,
  key: string,
  value: unknown,
): Record<string, unknown> {
  const loweredKey = key.toLowerCase();
  const shouldRedact = SENSITIVE_KEYS.some((fragment) => loweredKey.includes(fragment));

  if (shouldRedact) {
    accumulator[key] = '[redacted]';

    return accumulator;
  }

  if (typeof value === 'string') {
    accumulator[key] = value.includes('@') ? sanitizeEmail(value) : value;

    return accumulator;
  }

  if (typeof value === 'object' && value !== null) {
    accumulator[key] = '[object]';

    return accumulator;
  }

  accumulator[key] = value;

  return accumulator;
}
function formValueSummary(key: string): string {
  const loweredKey = key.toLowerCase();

  if (SENSITIVE_KEYS.some((fragment) => loweredKey.includes(fragment))) {
    return '[redacted]';
  }

  return '[provided]';
}
